import React, { useRef, useState, useEffect, Fragment } from "react";
import "./Map.css";
import * as ol from "ol";
import { defaults } from "ol/control";
import proj4 from "proj4";
import { register } from "ol/proj/proj4";
import MapControls from "./MapControls";
import MapContext from "./MapContext";
import { DragBox, DragPan } from "ol/interaction";
import SideBar from "./SideBar/SideBar";
import ToolBar from "./ToolBar";
import ScaleOverlays from "./ScaleOverlays/ScaleOverlays";
import { useDispatch, useSelector } from "react-redux";
import {
    addArcGISImage,
    addVector,
    addMapTiler_Street_Layer,
    addMapTiler_Basic_Layer,
    addMapTiler_Satellite_Layer,
    addOpenStreetMap_Layer,
    addBingMaps_RoadOnDemand,
    addBingMaps_Aerial,
    addBingMaps_AerialWithLabels,
    addBingMaps_CanvasDark,
    addDrawingLayer,
    addCoordinateLayer,
    getHiddenGroupLayers,
    addGPSLayer,
    addXYZLayer,
    addPlotCoordinateLayer,
} from "./utils/mapUtils";
import { inAndOut } from "ol/easing";
import LinkedMaps from "./LinkedMaps/LinkedMaps";
import { Splitter } from "@progress/kendo-react-layout";
import PrintBoxOverlay from "./Print/PrintBoxOverlay";
import { setSiteLoading } from "../redux/setConfigSlice";
import MapTipsOptions from "./SideBar/Results/Identify/MapTipsOptions";
import RightClickMenu from "./RightClickMenu";
import { setBaseMapLayer } from "../redux/baseMapConfigSlice";
import DataGrid from "./DataGrid/DataGrid";
import Mobile from "./Mobile/Mobile";
import { BiChevronsDown, BiChevronsUp } from "react-icons/bi";
import axios from "axios";
import HorizontalView from "./HorizontalView/HorizontalView";
import UrlQuery from "../Site/UrlQuery";
import { HandleAddLayers } from "./utils/addLayersUtils";

const mobile = window.innerWidth < 601 ? true : false;

const MapOverlays = () => {
    const sideBarOpen = useSelector((state) => state.toolSelect.sideBarOpen);

    return (
        <>
            <MapTipsOptions />
            {mobile ? (
                <>
                    <MapControls />
                    <PrintBoxOverlay />
                </>
            ) : (
                <>
                    <RightClickMenu />
                    <div className={`map-overlays ${sideBarOpen ? "" : "closed"}`}>
                        <PrintBoxOverlay />
                        <MapControls />
                        <ScaleOverlays />
                    </div>
                </>
            )}
        </>
    );
};

const Map = ({}) => {
    const dispatch = useDispatch();
    const mapConfig = useSelector((state) => state.configData.mapConfig);
    const layers = useSelector((state) => state.configData.layers);
    const thirdPartyBaseMap = useSelector((state) => state.configData.thirdPartyBaseMap);
    const layerProperties = useSelector(
        (state) => state.configData.vectorLayerProperties
    );
    const streetViewOn = useSelector((state) => state.toolSelect.streetView);
    const dataGridOn = useSelector((state) => state.toolSelect.dataGrid);
    const horizontalViewOn = useSelector((state) => state.toolSelect.horizontalView);
    const baseMaps = useSelector((state) => state.configData.baseMaps);
    const featureResults = useSelector((state) => state.resultsData.resultsForFeature);

    // Layer permissions from state
    const authorizedLayers = useSelector(
        (state) => state.authorizedPermissions.layerPermissions
    );

    const [map, setMap] = useState();
    const [panes, setPanes] = useState([
        { resizable: true },
        {
            resizable: true,
            collapsible: true,
            collapsed: true,
            size: "0%",
            max: "80%",
        },
    ]);

    const mapElement = useRef();

    useEffect(() => {
        let mapObject;
        if (mapConfig) {
            const projection = mapConfig.map.projection;
            const epsgCode = projection.split(":")[1];
            getProjectionString(epsgCode)
                .then((response) => {
                    proj4.defs([
                        [`ESRI:${epsgCode}`, response],
                        [`EPSG:${epsgCode}`, response],
                    ]);
                    register(proj4);
                    mapObject = new ol.Map({
                        view: new ol.View({
                            projection: projection,
                            enableRotation: false,
                        }),
                        controls: defaults({
                            zoom: false,
                            rotate: false,
                            attribution: false,
                        }),
                    });

                    // Add dragBox
                    const dragBox = new DragBox({});
                    dragBox.setActive(false);
                    dragBox.setProperties({ Id: "drag-box-zoom" });
                    mapObject.addInteraction(dragBox);

                    // Add scroll wheel drag
                    const scrollClickDrag = new DragPan({
                        condition: (e) => {
                            return e.originalEvent.which === 2;
                        },
                    });
                    scrollClickDrag.setProperties({ Id: "scroll-click-drag" });
                    mapObject.addInteraction(scrollClickDrag);

                    // Set target to html element
                    mapObject.setTarget(mapElement.current);

                    const numExtent = mapConfig.map.initialExtent
                        .split(",")
                        .map((e) => Number(e));

                    mapObject.getView().fit(numExtent, {
                        duration: 750,
                        easing: inAndOut,
                    });
                    setMap(mapObject);
                })
                .catch((error) => console.error(error));
        }
        // clear state when component unmounts
        // (return function runs on unmount)
        return () => {
            if (!mapObject) return;
            mapObject.setTarget(undefined);
        };
    }, [mapConfig]);

    const loading = useRef();
    useEffect(() => {
        loading.current = 0;
    }, []);

    const loadingStartCallback = () => {
        loading.current = loading.current + 1;
        dispatch(setSiteLoading(true));
    };
    const loadingFinishCallback = () => {
        loading.current = loading.current - 1;
        if (loading.current === 0) {
            dispatch(setSiteLoading(false));
        }
    };

    // Wait for map to be initialized and layers to be fetched
    // Add geojson layers to map
    useEffect(() => {
        if (map && layerProperties) {
            for (let i = baseMaps.length - 1; i >= 0; i--) {
                addArcGISImage(map, baseMaps[i]);
            }
            const notVisibleGroupLayers = getHiddenGroupLayers(layers);
            // Reverse order of adding layers so layers at top of config are on top
            let defaultZindex = 1;

            const layerAdder = new HandleAddLayers(
                map,
                loadingStartCallback,
                loadingFinishCallback
            );
            for (let i = layerProperties.length - 1; i >= 0; i--) {
                // check if layer is readable by checking for permission
                const layerReadPermission = authorizedLayers?.find(
                    (l) => l.layerId === layerProperties[i].Id
                )?.permissions?.Read;
                // if no permission, layer is readable, if permission use Read value
                const canReadLayer = layerReadPermission ?? true;
                // Continue to next iteration if layer is not readable
                if (!canReadLayer) {
                    continue;
                }
                const property = layerProperties[i];
                const Visible = notVisibleGroupLayers.includes(property.Id)
                    ? false
                    : property.Visible;
                // Add the layer
                layerAdder.addLayer(property, Visible, defaultZindex);
                defaultZindex++;
            }
            addDrawingLayer(map);
            addCoordinateLayer(map);
            addGPSLayer(map);
            addPlotCoordinateLayer(map);
        }
    }, [map, layerProperties]);

    useEffect(() => {
        if (map && layerProperties && thirdPartyBaseMap) {
            let arr = Object.getOwnPropertyNames(thirdPartyBaseMap);
            if (arr.includes("OpenStreetMap")) {
                addOpenStreetMap_Layer(map, thirdPartyBaseMap["OpenStreetMap"]);
                dispatch(
                    setBaseMapLayer({
                        name: "OpenStreetMap",
                        prop: thirdPartyBaseMap["OpenStreetMap"],
                    })
                );
            }
            if (arr.includes("MapTiler_Basic")) {
                addMapTiler_Basic_Layer(map, thirdPartyBaseMap["MapTiler_Basic"]);
                dispatch(
                    setBaseMapLayer({
                        name: "MapTiler_Basic",
                        prop: thirdPartyBaseMap["MapTiler_Basic"],
                    })
                );
            }
            if (arr.includes("MapTiler_Satellite")) {
                addMapTiler_Satellite_Layer(map, thirdPartyBaseMap["MapTiler_Satellite"]);
                dispatch(
                    setBaseMapLayer({
                        name: "MapTiler_Satellite",
                        prop: thirdPartyBaseMap["MapTiler_Satellite"],
                    })
                );
            }
            if (arr.includes("MapTiler_Steet")) {
                addMapTiler_Street_Layer(map, thirdPartyBaseMap["MapTiler_Steet"]);
                dispatch(
                    setBaseMapLayer({
                        name: "MapTiler_Steet",
                        prop: thirdPartyBaseMap["MapTiler_Steet"],
                    })
                );
            }
            if (process.env.REACT_APP_BING_KEY !== "REACT_APP_BING_KEY") {
                if (arr.includes("BingMaps_RoadOnDemand")) {
                    addBingMaps_RoadOnDemand(
                        map,
                        thirdPartyBaseMap["BingMaps_RoadOnDemand"]
                    );
                    dispatch(
                        setBaseMapLayer({
                            name: "BingMaps_RoadOnDemand",
                            prop: thirdPartyBaseMap["BingMaps_RoadOnDemand"],
                        })
                    );
                }
                if (arr.includes("BingMaps_Aerial")) {
                    addBingMaps_Aerial(map, thirdPartyBaseMap["BingMaps_Aerial"]);
                    dispatch(
                        setBaseMapLayer({
                            name: "BingMaps_Aerial",
                            prop: thirdPartyBaseMap["BingMaps_Aerial"],
                        })
                    );
                }
                if (arr.includes("BingMaps_AerialWithLabels")) {
                    addBingMaps_AerialWithLabels(
                        map,
                        thirdPartyBaseMap["BingMaps_AerialWithLabels"]
                    );
                    dispatch(
                        setBaseMapLayer({
                            name: "BingMaps_AerialWithLabels",
                            prop: thirdPartyBaseMap["BingMaps_AerialWithLabels"],
                        })
                    );
                }
                if (arr.includes("BingMaps_CanvasDark")) {
                    addBingMaps_CanvasDark(map, thirdPartyBaseMap["BingMaps_CanvasDark"]);
                    dispatch(
                        setBaseMapLayer({
                            name: "BingMaps_CanvasDark",
                            prop: thirdPartyBaseMap["BingMaps_CanvasDark"],
                        })
                    );
                }
            }
            if (arr.includes("XYZ_BaseMap")) {
                addXYZLayer(map, thirdPartyBaseMap["XYZ_BaseMap"]);
                const prop = thirdPartyBaseMap["XYZ_BaseMap"];
                dispatch(
                    setBaseMapLayer({
                        name: "XYZ_BaseMap",
                        prop: prop,
                        DisplayName: prop.DisplayName,
                    })
                );
            }
        }
    }, [map, layerProperties, thirdPartyBaseMap]);

    const handleSplitterChange = (e) => {
        setPanes(e.newState);
    };

    const handlePaneButton = () => {
        if (panes[1].size !== "0%") {
            setPanes([{}, { size: "0%", max: "80%" }]);
        } else {
            setPanes([{}, { size: "70%", max: "80%" }]);
        }
    };

    // handle street view and splitter
    useEffect(() => {
        if (mobile) {
            if (panes[1].size !== "0%") return;
            setPanes([{}, { size: "50%", max: "80%" }]);
        } else {
            if (streetViewOn || dataGridOn || horizontalViewOn) {
                setPanes([{}, { size: "30%", max: "80%" }]);
            } else {
                setPanes([{}, { size: "0%", max: "80%" }]);
            }
        }
    }, [streetViewOn, dataGridOn, horizontalViewOn]);

    return (
        <Fragment>
            <MapContext.Provider value={{ map }}>
                <UrlQuery />
                <ToolBar />
                {!mobile && <SideBar />}

                <Splitter
                    orientation={"vertical"}
                    style={{ zIndex: 1 }}
                    panes={panes}
                    onChange={handleSplitterChange}>
                    <div ref={mapElement} className="map-container">
                        <MapOverlays />
                        {mobile && (
                            <div
                                className="mobile-sidebar-button"
                                onClick={handlePaneButton}>
                                {panes[1].size === "0%" ? (
                                    <BiChevronsUp className="mobile-button-icon" />
                                ) : (
                                    <BiChevronsDown className="mobile-button-icon" />
                                )}
                            </div>
                        )}
                    </div>
                    {mobile ? (
                        <Mobile />
                    ) : (
                        <div style={{ height: "100%" }}>
                            {streetViewOn ? (
                                <LinkedMaps />
                            ) : dataGridOn ? (
                                <DataGrid />
                            ) : horizontalViewOn && featureResults?.length > 0 ? (
                                <HorizontalView />
                            ) : null}
                        </div>
                    )}
                </Splitter>
            </MapContext.Provider>
        </Fragment>
    );
};

export default Map;

/*
 * @param {*} espgCode - espg numerical projection code
 *  returns OGCWKT string
 */
export const getProjectionString = async (epsgCode) => {
    let projectionString;
    try {
        // Tries epsg.io first, (OpenLayers uses this website)
        const res0 = await axios.get(`https://epsg.io/${epsgCode}.wkt`);
        projectionString = res0.data;
    } catch (error) {
        console.log("epsg API call failed, trying spatialreference.org");
        // If first API call fails, spatialreference.org is used as fallback
        try {
            const res1 = await axios.get(
                `https://spatialreference.org/ref/esri/${epsgCode}/ogcwkt/`
            );
            projectionString = res1.data;
        } catch (error) {
            // Fallback to 3857 projection
            console.log(
                "invalid code, using 3857 (Psuedo-Mercator) as default projection"
            );
            projectionString =
                "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs";
        }
    }
    return projectionString;
};
